import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import { MPosContext } from 'utils/context';
import { getLabels, isEWPlan, isValidIMEI } from './utils';
import * as Styles from './styles';
import { Input } from '@acko-tech/building-blocks.ui.input';
import Checkbox from 'library/checkbox';
import { DeviceCategory, NewPlanStages } from '../constants';
import { Partners } from 'utils/constants';
import Image from 'next/image';
import { getImageBasePath } from 'utils/generics';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import SnackBar from 'library/snackbar';
import {
    SegmentEventNameTypes,
    SegmentEventButtonNames,
    SegmentEventFieldTypes,
    SegmentEventActionTypes,
    SegmentEventActionNames,
    PRODUCT,
    SUB_PRODUCT_NAME,
} from 'utils/segment/constants';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import {
    inputRegexValidation,
    phoneNumberValidation,
    emailValidation,
} from 'utils/validation';
import PlansHeader from '../plans/header';
import ModelDetails from 'components/modelDetails';

const CustomerDeviceDetails = () => {
    const router = useRouter();
    const {
        partnerId,
        planDetails,
        setPlanDetails,
        setActiveStage,
        metaData,
        permissions,
    } = useContext(MPosContext);
    const { isMobile } = useResponsiveDevice();

    const { customer, device, promoter } = getLabels(planDetails.device.type);
    const { promoter_id_visibility } = metaData;
    const {
        is_device_verified_in_model_page,
        name: model_name,
        serial_number: model_serial_number,
        activation_date: model_activation_date,
        show_activation_date,
    } = planDetails?.device || {};

    // state
    const [serialNumber, setSerialNumber] = useState<string>(
        planDetails.device.serial_number || ''
    );
    const [customerName, setCustomerName] = useState<string>(
        planDetails.customer.name || ''
    );
    const [customerPhone, setCustomerPhone] = useState<string>(
        planDetails.customer.phone || ''
    );
    const [promoterId, setPromoterId] = useState<string>(
        planDetails.device.promoter_id || ''
    );
    const [isChecked, setIsChecked] = useState(false);
    const [onClickSerialNumber, setOnClickSerialNumber] = useState(false);
    const [isDeviceVerified, setIsDeviceVerified] = useState(
        planDetails.device.is_device_verified
    );
    const [customerEmail, setCustomerEmail] = useState<string>(
        planDetails.customer.email || ''
    );
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [validationErr, setValidationErr] = useState({
        customer_name: '',
        phone: '',
        email: '',
        serial_number: '',
        confirm_serial_number: '',
    });
    const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
        useState(true);
    const [isVerifyingDevice, setIsVerifyingDevice] = useState(false);

    const { group } = permissions;

    useEffect(() => {
        if (onClickSerialNumber) {
            /**
             * Segment event
             */
            trackEvent(SegmentEventNameTypes.ACTION, {
                page_url: window?.location?.href || '',
                button_name: SegmentEventButtonNames.CLICK_SERIAL_NUMBER,
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.CLICK_SERIAL_NUMBER,
                product: PRODUCT,
                sub_product: SUB_PRODUCT_NAME,
                platform: isMobile ? 'mweb' : 'web',
                device: navigator.userAgent || '',
                partner: partnerId,
                field_selected: SegmentEventFieldTypes.TEXT,
                plan_id: planDetails.plan.plan_id,
                device_model: planDetails.device.name,
                category: planDetails.device.type,
            });
        }
    }, [onClickSerialNumber]);

    useEffect(() => {
        const shouldValidateSerialNumber =
            !planDetails?.device?.is_device_verified_in_model_page;

        const basicInputsValid =
            inputRegexValidation(serialNumber) &&
            inputRegexValidation(customerName) &&
            inputRegexValidation(customerPhone) &&
            phoneNumberValidation(customerPhone) &&
            isChecked;

        let shouldEnable = false;

        if (shouldValidateSerialNumber) {
            const imeiValid =
                Boolean(serialNumber) && isValidIMEI(serialNumber, partnerId);
            const baseValid =
                basicInputsValid &&
                Boolean(serialNumber) &&
                imeiValid &&
                isDeviceVerified;

            if (baseValid) {
                if ((customerEmail ?? '').length === 0) {
                    shouldEnable = true;
                } else {
                    shouldEnable = !!emailValidation(customerEmail);
                }
            } else {
                shouldEnable = false;
            }
        } else {
            const baseValid = basicInputsValid;

            if (!baseValid) {
                shouldEnable = false;
            } else {
                if ((customerEmail ?? '').length === 0) {
                    shouldEnable = true;
                } else {
                    shouldEnable = !!emailValidation(customerEmail);
                }
            }
        }

        if (promoter_id_visibility && (promoterId ?? '').length === 0) {
            shouldEnable = false;
        }

        setIsContinueButtonDisabled(!shouldEnable);
    }, [
        serialNumber,
        isChecked,
        isDeviceVerified,
        customerPhone,
        customerName,
        customerEmail,
        promoterId,
    ]);

    useEffect(() => {
        if (!isContinueButtonDisabled) {
            setValidationErr({
                ...validationErr,
                serial_number: '',
                confirm_serial_number: '',
                phone: '',
            });
        }
    }, [isContinueButtonDisabled]);

    useEffect(() => {
        if (customerEmail.length > 0) {
            if (!emailValidation(customerEmail)) {
                setValidationErr({
                    ...validationErr,
                    email: 'Enter valid email address',
                });
            } else {
                setValidationErr({
                    ...validationErr,
                    email: '',
                });
            }
        } else {
            setValidationErr({
                ...validationErr,
                email: '',
            });
        }
    }, [customerEmail]);

    useEffect(() => {
        if (customerPhone.length > 0) {
            if (!phoneNumberValidation(customerPhone)) {
                setValidationErr({
                    ...validationErr,
                    phone: 'Please enter a valid phone number',
                });
            } else {
                setValidationErr({
                    ...validationErr,
                    phone: '',
                });
            }
        } else {
            setValidationErr({
                ...validationErr,
                phone: '',
            });
        }
    }, [customerPhone]);

    useEffect(() => {
        const validationErrCopy = { ...validationErr };
        if (serialNumber.length === 0) {
            return;
        }
        if (!inputRegexValidation(serialNumber)) {
            if (partnerId === Partners.REALME) {
                validationErrCopy['serial_number'] =
                    'Please enter a valid serial number';
            } else {
                validationErrCopy['serial_number'] =
                    planDetails.device.type === 'mobile' ||
                    planDetails.device.type === 'tablet'
                        ? 'Please enter a valid 15 digit IMEI number'
                        : 'Please enter a valid serial number';
            }
            setValidationErr(validationErrCopy);
            setIsDeviceVerified(false);
            return;
        }

        if (partnerId === Partners.REALME) {
            if (!(serialNumber?.length >= 8 && serialNumber?.length <= 20)) {
                validationErrCopy['serial_number'] =
                    'Please enter a valid serial number';
                setIsDeviceVerified(false);
            } else {
                validationErrCopy['serial_number'] = '';
            }
        } else if (!isValidIMEI(serialNumber, partnerId)) {
            validationErrCopy['serial_number'] =
                planDetails.device.type === 'mobile' ||
                planDetails.device.type === 'tablet'
                    ? 'Please enter a valid 15 digit IMEI number'
                    : 'Please enter a valid serial number';
            setIsDeviceVerified(false);
            // setShowConfirmIMEI(false);
        } else {
            validationErrCopy['serial_number'] = '';
        }
        setValidationErr(validationErrCopy);
    }, [serialNumber]);

    const onChangeSerialNumber = (e: any) => {
        setSerialNumber(e.target.value);
        setIsDeviceVerified(false);
    };

    useEffect(() => {
        if (planDetails?.device?.is_device_verified_in_model_page) {
            return;
        }

        const passesOuter =
            isValidIMEI(serialNumber, partnerId) ||
            (partnerId === Partners.REALME &&
                serialNumber.length >= 8 &&
                serialNumber.length <= 20);

        const passesInner =
            isValidIMEI(serialNumber) ||
            (partnerId === Partners.REALME &&
                serialNumber.length >= 8 &&
                serialNumber.length <= 20);

        if (!passesOuter || !passesInner) {
            return;
        }

        setIsDeviceVerified(false);
        setIsVerifyingDevice(true);

        let cancelled = false;
        import('./apis').then(async (req) => {
            try {
                const { status, statusCode, errMsg } = await req.verifyDevice(
                    partnerId,
                    planDetails.device.type,
                    serialNumber,
                    isEWPlan(planDetails.plan.plan_id?.toLowerCase()),
                    planDetails.device.model_mapping_name,
                    planDetails.plan.plan_id,
                    planDetails.plan.program_id,
                    group
                );
                if (cancelled) {
                    return;
                }
                if (statusCode === 401) {
                    router.push('/login');
                    setSnackbar({
                        type: 'failure',
                        message: 'Token expired. Redirecting to login page.',
                    });
                    setIsDeviceVerified(false);
                    return;
                }
                setIsDeviceVerified(status);
                if (!status) {
                    const isRealmeOrMobile =
                        partnerId === Partners.REALME ||
                        planDetails?.device?.type === DeviceCategory.MOBILE;
                    setSnackbar({
                        type: 'failure',
                        message:
                            errMsg ||
                            (isRealmeOrMobile
                                ? 'Invalid serial number. Please check the serial number'
                                : 'Invalid IMEI. Please check the imei value'),
                    });
                    setValidationErr((prev) => ({
                        ...prev,
                        serial_number:
                            errMsg ||
                            (isRealmeOrMobile
                                ? 'Please enter a valid serial number'
                                : 'Please enter a valid IMEI number'),
                    }));
                }
            } finally {
                if (!cancelled) {
                    setIsVerifyingDevice(false);
                }
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        serialNumber,
        partnerId,
        planDetails.device.type,
        planDetails.device.model_mapping_name,
        planDetails.device.is_device_verified_in_model_page,
        planDetails.plan.plan_id,
        planDetails.plan.program_id,
    ]);

    const onSubmit = () => {
        setPlanDetails({
            ...planDetails,
            customer: {
                ...planDetails.customer,
                phone: customerPhone,
                name: customerName,
                email: customerEmail,
            },
            device: {
                ...planDetails.device,
                serial_number: serialNumber,
                is_device_verified: isDeviceVerified,
                promoter_id: promoter_id_visibility ? promoterId : '',
            },
        });
        setActiveStage(NewPlanStages.DOCUMENT_UPLOAD);
    };

    return (
        <>
            <PlansHeader />
            {promoter_id_visibility && (
                <Styles.Wrapper>
                    <Styles.EnterMoreDetails>
                        {promoter?.title}
                    </Styles.EnterMoreDetails>
                    <Styles.InputWrapper>
                        <Input
                            className=""
                            name={promoter.promoterIdLabel}
                            labelText={promoter.promoterIdLabel}
                            type={'text'}
                            value={promoterId}
                            onChange={(e) => setPromoterId(e.target.value)}
                        />
                    </Styles.InputWrapper>
                </Styles.Wrapper>
            )}
            <Styles.Wrapper>
                <Styles.EnterMoreDetails>
                    {customer.title}
                </Styles.EnterMoreDetails>
                <Styles.InputWrapper>
                    <Input
                        className=""
                        name={customer.nameLabel}
                        labelText={customer.nameLabel}
                        type={'text'}
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                    />
                </Styles.InputWrapper>
                <Styles.InputWrapper>
                    <Input
                        className=""
                        name={customer.mobileLabel}
                        labelText={customer.mobileLabel}
                        type={'text'}
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        leftElement={'+91'}
                        errMsg={validationErr.phone}
                        hasError={validationErr.phone.length > 0}
                    />
                </Styles.InputWrapper>
                <Styles.InputWrapper>
                    <Input
                        className=""
                        name={customer.emailLabel}
                        labelText={customer.emailLabel}
                        type={'text'}
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        errMsg={validationErr.email}
                        hasError={validationErr.email.length > 0}
                    />
                </Styles.InputWrapper>
            </Styles.Wrapper>
            <Styles.SplitFlex></Styles.SplitFlex>
            <Styles.Wrapper>
                <Styles.EnterMoreDetails>
                    {device.title}
                </Styles.EnterMoreDetails>

                {is_device_verified_in_model_page ? (
                    <ModelDetails
                        showActivationDate={show_activation_date}
                        modelName={model_name}
                        activationDate={model_activation_date}
                        imei={model_serial_number}
                    />
                ) : (
                    <>
                        {/* Imei NUmber */}
                        <Styles.InputWrapper
                            onClick={() => setOnClickSerialNumber(true)}
                        >
                            {isDeviceVerified ? (
                                <Input
                                    className={''}
                                    labelText={device.imeilabel}
                                    name={device.imeilabel}
                                    type={'text'}
                                    value={serialNumber}
                                    onChange={onChangeSerialNumber}
                                    errMsg={validationErr.serial_number}
                                    hasError={
                                        validationErr.serial_number.length > 0
                                    }
                                    disableCopy={true}
                                    disablePaste={true}
                                    leftElement={
                                        isDeviceVerified ? (
                                            <Styles.VerifiedIMEI>
                                                <Image
                                                    width={'16px'}
                                                    height={'16px'}
                                                    alt={''}
                                                    src={getImageBasePath(
                                                        '/icons/greenTickFill.svg'
                                                    )}
                                                />
                                            </Styles.VerifiedIMEI>
                                        ) : (
                                            <></>
                                        )
                                    }
                                />
                            ) : (
                                <Input
                                    className={''}
                                    labelText={device.imeilabel}
                                    name={device.imeilabel}
                                    type={'text'}
                                    value={serialNumber}
                                    onChange={onChangeSerialNumber}
                                    errMsg={validationErr.serial_number}
                                    hasError={
                                        validationErr.serial_number.length > 0
                                    }
                                    disableCopy={true}
                                    disablePaste={true}
                                    rightElement={
                                        isVerifyingDevice ? (
                                            <Image
                                                src={getImageBasePath(
                                                    '/gif/greenCircleLoader.gif'
                                                )}
                                                alt="Loading"
                                                width={20}
                                                height={20}
                                            />
                                        ) : null
                                    }
                                />
                            )}
                        </Styles.InputWrapper>
                        {isVerifyingDevice && (
                            <Styles.LoaderTextWrapper>
                                {'Verifying the IMEI number...'}
                            </Styles.LoaderTextWrapper>
                        )}
                    </>
                )}

                <Styles.InputWrapper>
                    <Styles.CheckBoxFlex>
                        <Checkbox
                            isSelected={isChecked}
                            onSelectHandler={() => setIsChecked((s) => !s)}
                            color={'#0FA457'}
                        />
                    </Styles.CheckBoxFlex>
                    <Styles.ConfirmationText>
                        {device.verifyLabel}
                    </Styles.ConfirmationText>
                </Styles.InputWrapper>
            </Styles.Wrapper>
            <Styles.ButtonWrapper>
                <PrimaryButton
                    disabled={isContinueButtonDisabled}
                    onClick={onSubmit}
                >
                    Continue
                </PrimaryButton>
            </Styles.ButtonWrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </>
    );
};

export default CustomerDeviceDetails;
